import { renderRcdIcons } from "../core/icons";
import * as rcdCalendar from "../core/calendar";
import { setupRcdMultiSelects } from "../core/components/multi-select";
import { setupRcdSearchSelects } from "../core/components/search-select";
import { setupRcdDropdownKits } from "../core/components/dropdown-kit";
import { openRcdDialog, closeRcdDialog } from "../core/components/dialog";

const setupJobEditor = () => {
    const editor = document.querySelector("[data-job-editor]");
    if (!editor || editor.dataset.jobEditorReady === "true") return;
    editor.dataset.jobEditorReady = "true";

    if (document.body.dataset.jobEditorOutsideCloseReady !== "true") {
        document.body.dataset.jobEditorOutsideCloseReady = "true";
        document.addEventListener("pointerdown", (event) => {
            document.querySelectorAll("[data-rcd-search-select]").forEach((root) => {
                if (root.contains(event.target)) return;
                const menu = root.querySelector("[data-rcd-search-select-menu]");
                const input = root.querySelector("[data-rcd-search-select-input]");
                if (menu) menu.hidden = true;
                input?.setAttribute("aria-expanded", "false");
            });
            document.querySelectorAll("[data-staff-picker]").forEach((picker) => {
                if (picker.contains(event.target)) return;
                const menu = picker.querySelector("[data-staff-search-menu]");
                const input = picker.querySelector("[data-staff-search-input]");
                if (menu) menu.hidden = true;
                input?.setAttribute("aria-expanded", "false");
            });
            document.querySelectorAll("[data-rcd-multi-select]").forEach((picker) => {
                if (picker.contains(event.target)) return;
                const menu = picker.querySelector("[data-rcd-multi-select-menu]");
                if (menu) menu.hidden = true;
            });
            document.querySelectorAll("[data-job-status-picker][open]").forEach((picker) => {
                if (!picker.contains(event.target)) picker.open = false;
            });
        }, true);
    }

    setupRcdSearchSelects(editor);

    const customerRoot = editor.querySelector("[data-rcd-customer-picker]");
    const customerValue = customerRoot?.querySelector("[data-rcd-search-select-value]");
    const siteRoot = editor.querySelector("[data-site-select]");
    const siteValue = siteRoot?.querySelector("[data-site-value]");
    const siteEmpty = siteRoot?.querySelector("[data-site-empty]");
    const siteAddress = editor.querySelector("[data-site-address]");
    const siteModal = document.querySelector("[data-site-modal]");
    const siteForm = siteModal?.querySelector("[data-site-form]");
    const siteStatus = siteModal?.querySelector("[data-site-status]");
    const siteTable = siteRoot?.querySelector(".user-job-site-table");
    const siteOptions = () => [...(siteRoot?.querySelectorAll("[data-site-option]") || [])];

    const filterSites = () => {
        const customerId = customerValue?.value || "";
        let visible = 0;
        siteOptions().forEach((option) => {
            const matches = option.dataset.customerId === customerId;
            option.hidden = !matches;
            if (matches) visible++;
        });
        if (siteEmpty) siteEmpty.hidden = visible > 0;
    };
    const chooseSite = (option) => {
        if (!siteValue) return;
        siteValue.value = option.dataset.id || "";
        siteOptions().forEach((item) => item.classList.toggle("is-selected", item === option));
        if (siteAddress) siteAddress.value = option.dataset.address || "";
    };
    siteRoot?.addEventListener("click", (event) => {
        const option = event.target.closest("[data-site-option]");
        if (option) chooseSite(option);
    });
    customerRoot?.addEventListener("rcd:customer-picker:change", (event) => {
        if (event.detail?.preserveDependentSelection) return;
        if (siteValue) siteValue.value = "";
        if (siteAddress) siteAddress.value = "";
        siteOptions().forEach((item) => item.classList.remove("is-selected"));
        filterSites();
    });
    filterSites();
    const selectedCustomerOption = () => customerRoot?.querySelector('[data-rcd-customer-option][data-value="' + (customerValue?.value || "") + '"]');
    editor.querySelector("[data-copy-customer-contact]")?.addEventListener("click", () => {
        const option = selectedCustomerOption();
        if (!option) return;
        const name = editor.querySelector("[data-contact-name]");
        const phone = editor.querySelector("[data-contact-phone]");
        const email = editor.querySelector("[data-contact-email]");
        if (name) name.value = option.dataset.contactName || "";
        if (phone) phone.value = option.dataset.contactPhone || "";
        if (email) email.value = option.dataset.contactEmail || "";
    });

    const openSiteModal = (event) => {
        if (!customerValue?.value) return;
        if (siteStatus) siteStatus.textContent = "";
        if (siteModal) openRcdDialog(siteModal, event?.currentTarget || document.activeElement);
    };
    const closeSiteModal = () => {
        if (siteModal) closeRcdDialog(siteModal);
        siteForm?.reset();
    };
    document.querySelectorAll("[data-site-open]").forEach((button) => button.addEventListener("click", openSiteModal));
    siteModal?.addEventListener("click", (event) => {
        if (event.target === siteModal || event.target.closest("[data-site-close]")) closeSiteModal();
    });
    siteForm?.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (!customerValue?.value) return;
        const payload = Object.fromEntries(new FormData(siteForm).entries());
        payload.customerId = customerValue.value;
        const token = editor.querySelector('input[name="__RequestVerificationToken"]')?.value || "";
        const response = await fetch("/Jobs/Edit?handler=CreateSite", {
            method: "POST",
            headers: { "Content-Type": "application/json", "RequestVerificationToken": token },
            body: JSON.stringify(payload)
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
            if (siteStatus) siteStatus.textContent = result.message || "Site could not be added.";
            return;
        }
        const option = document.createElement("button");
        option.type = "button";
        option.className = "user-job-site-row";
        option.dataset.siteOption = "";
        option.dataset.id = result.id;
        option.dataset.customerId = result.customerId;
        option.dataset.name = result.name;
        option.dataset.address = result.address;
        const strong = document.createElement("strong");
        strong.textContent = result.name;
        const address = document.createElement("span");
        address.textContent = result.address;
        const icon = document.createElement("i");
        icon.dataset.lucide = "check";
        option.append(strong, address, icon);
        siteTable?.insertBefore(option, siteEmpty || null);
        chooseSite(option);
        filterSites();
        closeSiteModal();
        renderRcdIcons();
    });

    const picker = editor.querySelector("[data-staff-picker]");
    if (!picker) return;
    const input = picker.querySelector("[data-staff-search-input]");
    const menu = picker.querySelector("[data-staff-search-menu]");
    const options = [...picker.querySelectorAll("[data-staff-option]")];
    const selected = picker.querySelector("[data-staff-selected]");
    const noResults = picker.querySelector("[data-staff-search-empty]");
    const selectedEmpty = picker.querySelector("[data-staff-selected-empty]");
    if (!input || !menu || !selected) return;

    const selectedIds = () => new Set([...selected.querySelectorAll("[data-staff-row]")].map((row) => row.dataset.id));
    const refresh = () => {
        const query = input.value.trim().toLowerCase();
        const ids = selectedIds();
        let visible = 0;
        options.forEach((option) => {
            const text = `${option.dataset.name || ""} ${option.dataset.email || ""}`.toLowerCase();
            const match = !ids.has(option.dataset.id) && (!query || text.includes(query));
            option.hidden = !match;
            if (match) visible++;
        });
        if (noResults) noResults.hidden = visible > 0;
        if (selectedEmpty) selectedEmpty.hidden = ids.size > 0;
    };
    const open = () => { refresh(); menu.hidden = false; input.setAttribute("aria-expanded", "true"); };
    const close = () => { menu.hidden = true; input.setAttribute("aria-expanded", "false"); };
    const addStaff = (option) => {
        if (selectedIds().has(option.dataset.id)) return;
        const row = document.createElement("tr");
        row.dataset.staffRow = "";
        row.dataset.id = option.dataset.id || "";
        const nameCell = document.createElement("td");
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "JobForm.AssignedStaffIds";
        hidden.value = option.dataset.id || "";
        const strong = document.createElement("strong");
        strong.textContent = option.dataset.name || "";
        nameCell.append(hidden, strong);
        const emailCell = document.createElement("td");
        emailCell.textContent = option.dataset.email || "";
        const actionCell = document.createElement("td");
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "rcd-icon-button";
        remove.dataset.staffRemove = "";
        remove.setAttribute("aria-label", `Remove ${option.dataset.name || "staff"}`);
        remove.innerHTML = '<i data-lucide="x"></i>';
        actionCell.append(remove);
        row.append(nameCell, emailCell, actionCell);
        selected.append(row);
        input.value = "";
        refresh();
        close();
        renderRcdIcons();
    };
    input.addEventListener("focus", open);
    input.addEventListener("input", open);
    options.forEach((option) => option.addEventListener("click", () => addStaff(option)));
    selected.addEventListener("click", (event) => {
        const button = event.target.closest("[data-staff-remove]");
        if (!button) return;
        button.closest("[data-staff-row]")?.remove();
        refresh();
    });
    document.addEventListener("click", (event) => { if (!picker.contains(event.target)) close(); });
    refresh();
};
const setupWorkspaceCalendars = (root = document) => {
    root.querySelectorAll("[data-workspace-calendar]:not([data-calendar-ready])").forEach((calendar) => {
        calendar.dataset.calendarReady = "true";

        const rawEvents = JSON.parse(calendar.querySelector("[data-calendar-events]")?.textContent || "[]");
        const events = rawEvents
            .map((item) => ({ ...item, start: new Date(item.start), end: new Date(item.end) }))
            .filter((item) => !Number.isNaN(item.start.getTime()) && !Number.isNaN(item.end.getTime()));

        let anchor = new Date(`${calendar.dataset.anchor || new Date().toISOString().slice(0, 10)}T12:00:00`);
        let pickerMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        let view = calendar.dataset.view || "week";
        if (view === "week" && window.matchMedia("(max-width: 700px)").matches) view = "day";
        else if (view === "week" && window.matchMedia("(max-width: 980px)").matches) view = "three-day";
        const baseHour = 7;
        const hourCount = 16;
        const slotHeightRem = 3.25;
        const canvas = calendar.querySelector("[data-calendar-canvas]");
        const title = calendar.querySelector("[data-calendar-title]");
        const agenda = calendar.querySelector("[data-calendar-agenda]");
        const scroll = calendar.querySelector("[data-calendar-scroll]");
        const viewLabel = calendar.querySelector("[data-calendar-view-label]");
        const viewMenu = calendar.querySelector("[data-calendar-view-menu]");
        const datePicker = calendar.querySelector("[data-calendar-date-picker]");
        const pickerTitle = calendar.querySelector("[data-calendar-picker-title]");
        const pickerGrid = calendar.querySelector("[data-calendar-date-grid]");

        const viewMeta = {
            day: { label: "Day", days: 1, step: 1, iconGroup: "day" },
            "three-day": { label: "3 Days", days: 3, step: 3, iconGroup: "day" },
            weekdays: { label: "Weekdays", days: 5, step: 7, iconGroup: "week" },
            week: { label: "Week", days: 7, step: 7, iconGroup: "week" },
            month: { label: "Month", days: 42, step: 1, iconGroup: "month" }
        };

        const { startOfDay, startOfWeek, addDays, sameDay, toLocalInputValue } = rcdCalendar;
        const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[char]));
                const formatTime = (date) => date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        const eventPayload = (event) => ({
            id: event.id || "",
            jobId: event.jobId || "",
            title: event.title || "",
            subtitle: event.subtitle || "",
            staffName: event.staffName || "",
            staffUserId: event.staffUserId || "",
            staffUserIds: Array.isArray(event.staffUserIds) ? event.staffUserIds.join(",") : (event.staffUserIds || ""),
            notes: event.notes || "",
            start: toLocalInputValue(event.start),
            end: toLocalInputValue(event.end),
            url: event.url || ""
        });
        const eventDataAttrs = (event) => Object.entries(eventPayload(event))
            .map(([key, value]) => ` data-event-${key.replace(/[A-Z]/g, (char) => "-" + char.toLowerCase())}="${escapeHtml(value)}"`)
            .join("");
        const normalizedView = () => viewMeta[view] ? view : "week";
        const viewDays = () => viewMeta[normalizedView()].days;
        const timedStart = () => {
            if (view === "day" || view === "three-day") return startOfDay(anchor);
            return startOfWeek(anchor);
        };
        const periodStep = () => viewMeta[normalizedView()].step;
        const toneFor = (event) => {
            const key = `${event.staffName || "unassigned"}-${event.title || "job"}`;
            let hash = 0;
            for (let index = 0; index < key.length; index += 1) {
                hash = (hash * 31 + key.charCodeAt(index)) % 997;
            }
            return `tone-${hash % 5}`;
        };
        const eventsInRange = (start, end) => events.filter((event) => event.start < end && event.end >= start).sort((a, b) => a.start - b.start);
        const visibleRange = () => {
            if (view === "month") {
                const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
                const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
                return { start, end, days: [] };
            }
            const start = timedStart();
            const days = Array.from({ length: viewDays() }, (_, index) => addDays(start, index));
            return { start, end: addDays(start, viewDays()), days };
        };

        const closeFloatingControls = (except = null) => {
            [viewMenu, datePicker].forEach((control) => {
                if (control && control !== except) control.open = false;
            });
        };

        const renderAgenda = () => {
            const range = visibleRange();
            const visibleEvents = eventsInRange(range.start, range.end);
            agenda.innerHTML = visibleEvents.length
                ? visibleEvents.map((event) => `<button type="button" class="workspace-calendar-agenda-item"${eventDataAttrs(event)} aria-label="Edit ${escapeHtml(event.title)} appointment"><span class="workspace-calendar-agenda-dot"></span><strong>${escapeHtml(event.staffName || "Unassigned")}</strong><time>${escapeHtml(event.start.toLocaleDateString([], { day: "2-digit", month: "short" }))} ${escapeHtml(formatTime(event.start))}</time><small>${escapeHtml(event.subtitle || event.title)}</small></button>`).join("")
                : '<p class="workspace-calendar-empty">No appointments in this period.</p>';
        };

        const eventUrl = (event) => {
            const raw = String(event.url || "").trim();
            return raw && (raw.startsWith("/") || raw.startsWith("#") || raw.startsWith(window.location.origin)) ? raw : "";
        };

        const eventMarkup = (event, lane = 0, lanes = 1) => {
            const start = event.start.getHours() + event.start.getMinutes() / 60;
            const duration = Math.max(0.5, (event.end - event.start) / 36e5);
            const boundedStart = Math.max(0, start - baseHour);
            return `<button type="button" class="workspace-calendar-event ${toneFor(event)}"${eventDataAttrs(event)} style="--event-start:${boundedStart};--event-duration:${duration};--event-lane:${lane};--event-lanes:${lanes}" aria-label="Edit ${escapeHtml(event.title)} appointment"><strong>${escapeHtml(event.title)}</strong><span>${escapeHtml(event.staffName || "Unassigned")}</span><small>${escapeHtml(event.subtitle || `${formatTime(event.start)} - ${formatTime(event.end)}`)}</small></button>`;
        };

        const layoutTimedEvents = (dayEvents) => {
            const sorted = [...dayEvents].sort((a, b) => a.start - b.start || a.end - b.end);
            const lanes = [];
            return sorted.map((event) => {
                const laneIndex = lanes.findIndex((laneEnd) => laneEnd <= event.start);
                const index = laneIndex === -1 ? lanes.length : laneIndex;
                lanes[index] = event.end;
                return { event, lane: index, lanes };
            }).map((item) => ({ ...item, laneCount: Math.max(1, item.lanes.length) }));
        };

        const nowLineMarkup = (day) => {
            const now = new Date();
            if (!sameDay(now, day)) return "";
            const offset = now.getHours() + now.getMinutes() / 60 - baseHour;
            if (offset < 0 || offset > hourCount) return "";
            return `<div class="workspace-calendar-now-line" style="--now-offset:${offset}"><span></span></div>`;
        };

        const renderDayColumns = (days) => {
            const base = days[0];
            canvas.className = `workspace-calendar-canvas is-${normalizedView()}`;
            canvas.style.setProperty("--calendar-days", String(days.length));
            const axis = `<div class="workspace-calendar-time-axis"><span></span>${Array.from({ length: hourCount }, (_, index) => `<span>${escapeHtml(new Date(2000, 0, 1, baseHour + index).toLocaleTimeString([], { hour: "numeric" }))}</span>`).join("")}</div>`;
            const columns = days.map((day) => {
                const dayEvents = layoutTimedEvents(events.filter((event) => sameDay(event.start, day)));
                return `<section class="workspace-calendar-day${sameDay(day, new Date()) ? " is-today" : ""}"><header><strong>${escapeHtml(day.toLocaleDateString([], { weekday: "short" }))}</strong><span>${escapeHtml(day.toLocaleDateString([], { day: "numeric" }))}</span></header><div class="workspace-calendar-day-body">${nowLineMarkup(day)}${dayEvents.map((item) => eventMarkup(item.event, item.lane, item.laneCount)).join("")}</div></section>`;
            }).join("");
            canvas.innerHTML = axis + columns;
            title.textContent = view === "day"
                ? base.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" })
                : `${base.toLocaleDateString([], { month: "short", day: "numeric" })} - ${days[days.length - 1].toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}`;
        };

        const renderMonth = () => {
            const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
            const gridStart = startOfWeek(first);
            const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
            title.textContent = anchor.toLocaleDateString([], { month: "long", year: "numeric" });
            canvas.className = "workspace-calendar-canvas is-month";
            canvas.style.setProperty("--calendar-days", "7");
            canvas.innerHTML = `<div class="workspace-calendar-month-head">${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label) => `<span>${label}</span>`).join("")}</div>` + days.map((day) => {
                const dayEvents = events.filter((event) => sameDay(event.start, day));
                return `<section class="workspace-calendar-month-day ${day.getMonth() === anchor.getMonth() ? "" : "is-outside"} ${sameDay(day, new Date()) ? "is-today" : ""}"><header>${day.getDate()}</header>${dayEvents.slice(0, 3).map((event) => `<button type="button"${eventDataAttrs(event)}><strong>${escapeHtml(event.title)}</strong><span>${escapeHtml(formatTime(event.start))}</span></button>`).join("")}${dayEvents.length > 3 ? `<small>+${dayEvents.length - 3} more</small>` : ""}</section>`;
            }).join("");
        };

        const renderDatePicker = () => {
            if (!pickerGrid || !pickerTitle) return;
            const first = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), 1);
            const gridStart = startOfWeek(first);
            const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
            pickerTitle.textContent = pickerMonth.toLocaleDateString([], { month: "long", year: "numeric" });
            pickerGrid.innerHTML = days.map((day) => {
                const iso = day.toISOString().slice(0, 10);
                const outside = day.getMonth() !== pickerMonth.getMonth();
                return `<button type="button" data-calendar-pick-date="${iso}" class="${outside ? "is-outside" : ""} ${sameDay(day, anchor) ? "is-selected" : ""} ${sameDay(day, new Date()) ? "is-today" : ""}">${day.getDate()}</button>`;
            }).join("");
        };

        const render = () => {
            view = normalizedView();
            calendar.dataset.view = view;
            if (viewLabel) viewLabel.textContent = viewMeta[view].label;
            calendar.querySelectorAll("[data-calendar-view]").forEach((button) => {
                const isIconSwitch = button.closest(".workspace-calendar-view-switch") !== null;
                const selected = isIconSwitch
                    ? viewMeta[view].iconGroup === button.dataset.calendarView
                    : button.dataset.calendarView === view;
                button.classList.toggle("is-active", selected);
                button.setAttribute("aria-checked", selected ? "true" : "false");
            });
            if (view === "month") {
                renderMonth();
            } else {
                const first = timedStart();
                renderDayColumns(Array.from({ length: viewDays() }, (_, index) => addDays(first, index)));
            }
            renderAgenda();
            pickerMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
            renderDatePicker();
            if (scroll && view !== "month") {
                scroll.scrollTop = Math.max(0, (8 - baseHour) * slotHeightRem * 16);
            }
            renderRcdIcons();
        };

        calendar.addEventListener("toggle", (event) => {
            const details = event.target.closest?.("[data-calendar-view-menu], [data-calendar-date-picker]");
            if (details?.open) {
                closeFloatingControls(details);
                if (details === datePicker) renderDatePicker();
            }
        }, true);

        calendar.addEventListener("click", (event) => {
            const eventButton = event.target.closest("[data-event-id]");
            if (eventButton) {
                event.preventDefault();
                closeFloatingControls();
                calendar.dispatchEvent(new CustomEvent("workspace-calendar:event-edit", { bubbles: true, detail: { ...eventButton.dataset, opener: eventButton } }));
                return;
            }
            const viewButton = event.target.closest("[data-calendar-view]");
            if (viewButton) {
                view = viewButton.dataset.calendarView;
                closeFloatingControls();
                render();
                return;
            }
            if (event.target.closest("[data-calendar-refresh]")) { render(); return; }
            if (event.target.closest("[data-calendar-today]")) { anchor = new Date(); closeFloatingControls(); render(); return; }
            if (event.target.closest("[data-calendar-previous]")) { view === "month" ? anchor.setMonth(anchor.getMonth() - 1) : anchor.setDate(anchor.getDate() - periodStep()); closeFloatingControls(); render(); return; }
            if (event.target.closest("[data-calendar-next]")) { view === "month" ? anchor.setMonth(anchor.getMonth() + 1) : anchor.setDate(anchor.getDate() + periodStep()); closeFloatingControls(); render(); return; }
            if (event.target.closest("[data-calendar-picker-previous]")) { pickerMonth.setMonth(pickerMonth.getMonth() - 1); renderDatePicker(); return; }
            if (event.target.closest("[data-calendar-picker-next]")) { pickerMonth.setMonth(pickerMonth.getMonth() + 1); renderDatePicker(); return; }
            const pickedDate = event.target.closest("[data-calendar-pick-date]");
            if (pickedDate) {
                anchor = new Date(`${pickedDate.dataset.calendarPickDate}T12:00:00`);
                closeFloatingControls();
                render();
            }
        });

        document.addEventListener("pointerdown", (event) => {
            if (!calendar.contains(event.target)) closeFloatingControls();
        });
        calendar.addEventListener("keydown", (event) => {
            if (event.key === "Escape") closeFloatingControls();
        });

        render();
    });
};const setupJobStatusPicker = () => {
    const picker = document.querySelector("[data-job-status-picker]");
    if (!picker || picker.dataset.ready === "true") return;
    picker.dataset.ready = "true";

    picker.addEventListener("click", (event) => {
        const button = event.target.closest("[data-job-status-value]");
        if (!button) return;
        event.preventDefault();
        const value = button.dataset.jobStatusValue;
        const field = document.querySelector('#job-main-form select[name="JobForm.Status"]');
        if (field) { field.value = value; field.dispatchEvent(new Event("change", { bubbles: true })); }
        const label = picker.querySelector("[data-job-status-label]");
        if (label) label.textContent = button.textContent.trim();
        picker.querySelectorAll("[data-job-status-value]").forEach((item) => item.classList.toggle("is-selected", item === button));
        picker.open = false;
    });

    document.addEventListener("pointerdown", (event) => {
        if (!picker.contains(event.target)) picker.open = false;
    }, true);
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") picker.open = false;
    });
};
const setCalendarDialogField = (form, name, value) => {
    const field = form?.elements.namedItem(name);
    if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) {
        field.value = value ?? "";
        field.dispatchEvent(new Event("change", { bubbles: true }));
    }
};

const openScheduleAppointmentDialog = (dialog, payload = {}, opener = document.activeElement) => {
    const form = dialog?.querySelector("form");
    if (!(dialog instanceof HTMLElement) || !(form instanceof HTMLFormElement)) return;
    const isEdit = Boolean(payload.eventId || payload.id);
    form.reset();
    setCalendarDialogField(form, "ScheduleForm.AppointmentId", payload.eventId || payload.id || "");
    if (isEdit) {
        setCalendarDialogField(form, "ScheduleForm.JobId", payload.eventJobId || payload.jobId || "");
        setCalendarDialogField(form, "ScheduleForm.ScheduledStartUtc", payload.eventStart || payload.start || "");
        setCalendarDialogField(form, "ScheduleForm.ScheduledEndUtc", payload.eventEnd || payload.end || "");
        setCalendarDialogField(form, "ScheduleForm.Task", payload.eventSubtitle || payload.subtitle || "");
        setCalendarDialogField(form, "ScheduleForm.Notes", payload.eventNotes || payload.notes || "");
    }
    const staffValues = String(
        payload.eventStaffUserIds
        || payload.staffUserIds
        || payload.eventStaffUserId
        || payload.staffUserId
        || ""
    ).split(",").map((value) => value.trim()).filter(Boolean);
    dialog.querySelector("[data-rcd-multi-select]")?.dispatchEvent(new CustomEvent("rcd:multi-select:set", {
        detail: { values: staffValues }
    }));
    const title = dialog.querySelector("#schedule-appointment-title");
    if (title) title.textContent = isEdit ? "Edit Appointment" : "New Appointment";
    openRcdDialog(dialog, opener);
};

const setupCalendarDialogs = () => {
    if (document.body.dataset.calendarDialogsReady === "true") return;
    document.body.dataset.calendarDialogsReady = "true";
    document.addEventListener("click", (event) => {
        if (!(event.target instanceof Element)) return;
        const open = event.target.closest("[data-workspace-calendar] [data-job-dialog-open='appointment']");
        if (open && !open.closest("[data-job-tab-host]")) {
            const dialog = document.querySelector('[data-job-dialog="appointment"]');
            if (dialog) openScheduleAppointmentDialog(dialog, {}, open);
            return;
        }
    });
    document.addEventListener("workspace-calendar:event-edit", (event) => {
        if (!(event.target instanceof Element) || event.target.closest("[data-job-tab-host]")) return;
        const dialog = document.querySelector('[data-job-dialog="appointment"]');
        if (dialog) openScheduleAppointmentDialog(dialog, event.detail || {}, event.detail?.opener || event.target);
    });
};
const setupJobTabs = () => {
    const tabs = document.querySelector("[data-job-tabs]");
    const editor = document.querySelector("[data-job-editor]");
    const host = document.querySelector("[data-job-tab-host]");
    if (!tabs || !editor || !host || tabs.dataset.jobTabsReady === "true" || !tabs.dataset.jobId) return;
    tabs.dataset.jobTabsReady = "true";
    const jobId = tabs.dataset.jobId;
    const token = editor.querySelector('input[name="__RequestVerificationToken"]')?.value || "";
    let activeTab = "job", searchTimer = 0;
    const activate = (tab) => {
        activeTab = tab;
        tabs.querySelectorAll("[data-job-tab]").forEach((x) => x.classList.toggle("is-active", x.dataset.jobTab === tab));
        editor.hidden = tab !== "job";
        host.hidden = tab === "job";
    };
    const load = async (tab, query = {}) => {
        const panel = host.querySelector("[data-job-tab-panel]");
        activate(tab);
        host.innerHTML = '<div class="user-job-tab-loading"><span></span><p>Loading job details…</p></div>';
        const url = new URL(location.href);
        url.hash = "";
        url.searchParams.set("handler", "Tab"); url.searchParams.set("id", jobId); url.searchParams.set("tab", tab);
        if (typeof query === "string") query = { search: query };
        const state = { search: panel?.dataset.search || "", sortBy: panel?.dataset.sortBy || "date", sortDirection: panel?.dataset.sortDirection || "desc", pageNumber: panel?.dataset.pageNumber || "1", pageSize: panel?.dataset.pageSize || "25", ...query };
        Object.entries(state).forEach(([key, value]) => { if (value !== "" && value != null) url.searchParams.set(key, value); });
        const response = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!response.ok) throw new Error("The job tab could not be loaded.");
        host.innerHTML = await response.text();
        setupWorkspaceCalendars(host);
        setupRcdMultiSelects(host, renderRcdIcons);
        setupRcdDropdownKits(host, renderRcdIcons);
        history.replaceState({}, "", location.pathname + "?id=" + encodeURIComponent(jobId) + "#job-" + tab);
        renderRcdIcons();
    };
    const post = async (handler, payload) => {
        const response = await fetch(location.pathname + "?handler=" + encodeURIComponent(handler), {
            method: "POST", headers: { "Content-Type": "application/json", "RequestVerificationToken": token, "X-Requested-With": "XMLHttpRequest" },
            body: JSON.stringify(payload)
        });
        const result = await response.json().catch(() => ({ message: "The action could not be completed." }));
        if (!response.ok) throw new Error(result.message || "The action could not be completed.");
        return result;
    };
    const uploadJobFiles = async (form) => {
        const files = [...(form.querySelector("[data-job-note-files]")?.files || [])];
        const urls = [];
        for (const file of files) {
            if (file.size > 20 * 1024 * 1024) throw new Error(`${file.name} is larger than 20 MB.`);
            const body = new FormData();
            body.append("file", file);
            body.append("panel", `jobs/${jobId}/notes`);
            const response = await fetch("/api/storage/upload", { method: "POST", body });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.message || `${file.name} could not be uploaded.`);
            urls.push(result.publicUrl);
        }
        return urls;
    };
    const formJson = (form) => {
        const data = {};
        new FormData(form).forEach((value, key) => {
            const field = form.elements.namedItem(key);
            let normalized;
            if (field?.type === "number") normalized = value === "" ? 0 : Number(value);
            else if (field?.type === "datetime-local") normalized = value ? new Date(value).toISOString() : null;
            else normalized = value === "" && (key.endsWith("Id") || key.endsWith("UserId")) ? null : value;
            if (Object.prototype.hasOwnProperty.call(data, key)) data[key] = Array.isArray(data[key]) ? [...data[key], normalized] : [data[key], normalized];
            else data[key] = normalized;
        });
        return data;
    };
    const setAppointmentField = (form, name, value) => {
        const field = form?.elements.namedItem(name);
        if (!field) return;
        field.value = value ?? "";
    };
    const clearPersonPicker = (picker) => {
        const selected = picker?.querySelector("[data-rcd-multi-select-selected]");
        selected?.querySelectorAll(".rcd-multi-select__item").forEach((row) => row.remove());
        const empty = selected?.querySelector("[data-rcd-multi-select-selected-empty]");
        if (empty) empty.hidden = false;
        const input = picker?.querySelector("[data-rcd-multi-select-search]");
        if (input) input.value = "";
    };
    const getAppointmentStaffMap = () => {
        try {
            return JSON.parse(host.querySelector("[data-appointment-staff-map]")?.textContent || "{}");
        } catch {
            return {};
        }
    };
    const addPersonToPicker = (picker, id, name, email) => {
        if (!picker || !id) return;
        const selected = picker.querySelector("[data-rcd-multi-select-selected]");
        if (!selected) return;
        if (selected.querySelector(`.rcd-multi-select__item[data-id="${CSS.escape(id)}"]`)) return;
        const option = picker.querySelector(`[data-rcd-multi-select-option][data-id="${CSS.escape(id)}"]`);
        const displayName = name || option?.dataset.name || "Staff member";
        const displayEmail = email || option?.dataset.email || "";
        const row = document.createElement("div");
        row.className = "rcd-multi-select__item";
        row.dataset.id = id;
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "StaffUserIds";
        hidden.value = id;
        const avatar = document.createElement("span");
        avatar.className = "rcd-avatar rcd-avatar--sm";
        avatar.textContent = displayName.trim().slice(0, 1).toUpperCase() || "?";
        const copy = document.createElement("span");
        copy.className = "rcd-multi-select__copy";
        const strong = document.createElement("strong");
        strong.textContent = displayName;
        const small = document.createElement("small");
        small.textContent = displayEmail;
        copy.append(strong, small);
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "rcd-icon-button";
        remove.dataset.personRemove = "";
        remove.setAttribute("aria-label", `Remove ${displayName}`);
        remove.innerHTML = '<i data-lucide="x"></i>';
        row.append(hidden, avatar, copy, remove);
        selected.append(row);
        const empty = selected.querySelector("[data-rcd-multi-select-selected-empty]");
        if (empty) empty.hidden = true;
        renderRcdIcons();
    };
    const openAppointmentDialog = (payload = {}) => {
        const dialog = host.querySelector('[data-job-dialog="appointment"]');
        const form = dialog?.querySelector("form");
        if (!dialog || !form) return;
        form.reset();
        const picker = dialog.querySelector("[data-rcd-multi-select]");
        clearPersonPicker(picker);
        const isEdit = Boolean(payload.eventId || payload.id);
        setAppointmentField(form, "Id", payload.eventId || payload.id || "");
        setAppointmentField(form, "JobId", payload.eventJobId || payload.jobId || jobId);
        setAppointmentField(form, "StartUtc", payload.eventStart || "");
        setAppointmentField(form, "EndUtc", payload.eventEnd || "");
        setAppointmentField(form, "Task", payload.eventSubtitle || payload.subtitle || "");
        setAppointmentField(form, "Notes", payload.eventNotes || payload.notes || "");
        const appointmentId = payload.eventId || payload.id || "";
        const mappedStaff = appointmentId ? (getAppointmentStaffMap()[appointmentId] || []) : [];
        if (mappedStaff.length > 0) {
            mappedStaff.forEach((staff) => addPersonToPicker(picker, staff.id || staff.userId || "", staff.name || "", staff.email || ""));
        } else {
            addPersonToPicker(picker, payload.eventStaffUserId || payload.staffUserId || "", payload.eventStaffName || payload.staffName || "", "");
        }
        const title = dialog.querySelector("#job-appointment-title");
        if (title) title.textContent = isEdit ? "Edit Appointment" : "New Appointment";
        openRcdDialog(dialog);
    };    tabs.addEventListener("click", (event) => {
        const button = event.target.closest("[data-job-tab]");
        if (!button || button.disabled) return;
        const tab = button.dataset.jobTab;
        if (tab === "job") { activate("job"); history.replaceState({}, "", location.pathname + "?id=" + encodeURIComponent(jobId) + "#job-information"); }
        else load(tab).catch((error) => host.innerHTML = '<div class="rcd-alert warning">' + error.message + '</div>');
    });
    document.querySelector("[data-job-open-tab='time']")?.addEventListener("click", () => {
        load("time").catch((error) => host.innerHTML = '<div class="rcd-alert warning">' + error.message + '</div>');
    });
    document.querySelector("[data-job-open-appointment]")?.addEventListener("click", async () => {
        try {
            await load("schedule");
            openAppointmentDialog();
        } catch (error) {
            host.innerHTML = '<div class="rcd-alert warning">' + error.message + '</div>';
        }
    });
    host.addEventListener("workspace-calendar:event-edit", (event) => {
        event.preventDefault();
        openAppointmentDialog(event.detail || {});
    });    host.addEventListener("click", (event) => {
        const taskOpen = event.target.closest("[data-task-dialog-open]");
        if (taskOpen) { const task = host.querySelector("[data-task-dialog]"); if(task) openRcdDialog(task, taskOpen); return; }
        if (event.target.closest("[data-task-dialog-save]")) { const task=event.target.closest("[data-task-dialog]"); const name=task?.querySelector("[data-task-name]")?.value.trim(); if(!name){task?.querySelector("[data-task-name]")?.focus();return;} const appointment=host.querySelector('[data-job-dialog="appointment"]'); const target=appointment?.querySelector("[data-appointment-task-name]"); const description=task.querySelector("[data-task-description]")?.value.trim(); const due=task.querySelector("[data-task-due]")?.value; const cost=task.querySelector("[data-task-cost]")?.value; if(target)target.value=[name,description,due?`Due ${due}`:"",cost?`Estimated ${cost}`:""].filter(Boolean).join(" - "); closeRcdDialog(task); }
    });
    host.addEventListener("click", async (event) => {
        const open = event.target.closest("[data-job-dialog-open]");
        if (open) {
            if (open.dataset.jobDialogOpen === "appointment") {
                openAppointmentDialog();
            } else {
                const dialog = host.querySelector('[data-job-dialog="' + open.dataset.jobDialogOpen + '"]');
                if (dialog) openRcdDialog(dialog, open);
            }
            return;
        }
        const invoiceType = event.target.closest("[data-invoice-type-option]");
        if (invoiceType) {
            const dialog = invoiceType.closest("[data-job-dialog='invoice']");
            const form = dialog?.querySelector("[data-job-invoice-form]");
            const selectedType = invoiceType.dataset.invoiceTypeOption || "";
            dialog?.querySelectorAll("[data-invoice-type-option]").forEach((button) => {
                button.setAttribute("aria-selected", button === invoiceType ? "true" : "false");
            });
            dialog?.querySelectorAll("[data-invoice-type-panel]").forEach((panel) => {
                panel.hidden = panel.dataset.invoiceTypePanel !== selectedType;
            });
            const empty = dialog?.querySelector("[data-invoice-type-empty]");
            if (empty) empty.hidden = true;
            const field = form?.elements.namedItem("InvoiceType");
            if (field) field.value = selectedType;
            const submit = form?.querySelector("[type=submit]");
            const panel = dialog?.querySelector(`[data-invoice-type-panel="${CSS.escape(selectedType)}"]`);
            if (submit) submit.disabled = panel?.dataset.invoiceTypeAvailable !== "true";
            return;
        }
        const sort = event.target.closest("[data-job-sort]");
        if (sort) {
            const panel = host.querySelector("[data-job-tab-panel]");
            const nextDirection = panel?.dataset.sortBy === sort.dataset.jobSort && panel?.dataset.sortDirection === "asc" ? "desc" : "asc";
            await load(activeTab, { sortBy: sort.dataset.jobSort, sortDirection: nextDirection, pageNumber: 1 }); return;
        }
        const page = event.target.closest("[data-job-page]");
        if (page && !page.disabled) { await load(activeTab, { pageNumber: page.dataset.jobPage }); return; }
        const toggle = event.target.closest("[data-job-cost-menu-toggle]");
        if (toggle) { const menu = toggle.parentElement.querySelector("[data-job-cost-menu]"); if (menu) menu.hidden = !menu.hidden; return; }
        const remove = event.target.closest("[data-job-delete]");
        if (remove) {
            remove.disabled = true;
            try { await post("Delete" + remove.dataset.kind, { jobId, itemId: remove.dataset.id }); await load(activeTab, { pageNumber: host.querySelector("[data-job-tab-panel]")?.dataset.pageNumber || 1 }); }
            catch (error) { remove.disabled = false; host.querySelector("[data-job-tab-status]").textContent = error.message; }
            return;
        }
        const price = event.target.closest("[data-price-add]");
        if (price) {
            price.disabled = true;
            try { await post("AddCost", { jobId, priceListItemId: price.dataset.id, quantity: 1, unitCost: 0, unitSellPrice: 0, taxRate: 0 }); await load("costs"); }
            catch (error) { price.disabled = false; host.querySelector("[data-job-tab-status]").textContent = error.message; }
        }
    });
    host.addEventListener("submit", async (event) => {
        const invoiceForm = event.target.closest("[data-job-invoice-form]");
        if (invoiceForm) {
            event.preventDefault();
            const submit = invoiceForm.querySelector("[type=submit]");
            if (submit) submit.disabled = true;
            try {
                const result = await post("CreateInvoice", formJson(invoiceForm));
                if (result.location) location.assign(result.location);
                else await load("invoices");
            } catch (error) {
                if (submit) submit.disabled = false;
                const status = host.querySelector("[data-job-tab-status]");
                if (status) status.textContent = error.message;
            }
            return;
        }
        const form = event.target.closest("[data-job-ajax-form]");
        if (!form) return;
        event.preventDefault();
        const submit = form.querySelector("[type=submit]");
        if (submit) submit.disabled = true;
        try {
            const payload = formJson(form);
            if (form.dataset.handler === "AddNote") payload.AttachmentUrls = await uploadJobFiles(form);
            await post(form.dataset.handler, payload);
            await load(form.dataset.tab);
        }
        catch (error) { if (submit) submit.disabled = false; host.querySelector("[data-job-tab-status]").textContent = error.message; }
    });
    host.addEventListener("click", (event) => {
        const select = event.target.closest("[data-job-file-select]");
        if (select) select.closest("form")?.querySelector("[data-job-note-files]")?.click();
    });
    host.addEventListener("change", (event) => {
        if (event.target.matches("[data-job-page-size]")) load(activeTab, { pageSize: event.target.value, pageNumber: 1 }).catch(() => {});
        if (event.target.matches("[data-invoice-cutoff-toggle]")) {
            const field = event.target.closest("[data-invoice-type-panel]")?.querySelector("[data-invoice-cutoff-date]");
            if (field) field.disabled = !event.target.checked;
        }
    });
    host.addEventListener("input", (event) => {
        if (event.target.matches("[data-job-tab-search]")) { clearTimeout(searchTimer); searchTimer = setTimeout(() => load(activeTab, { search: event.target.value.trim(), pageNumber: 1 }).catch(() => {}), 300); }
        if (event.target.matches("[data-price-search]")) { const term = event.target.value.trim().toLowerCase(); host.querySelectorAll("[data-price-add]").forEach((x) => x.hidden = !x.textContent.toLowerCase().includes(term)); }
    });
    const initial = location.hash.replace("#job-", "");
    if (["notes", "schedule", "time", "costs", "invoices", "quotes"].includes(initial)) load(initial).catch(() => activate("job"));
};

export {
    setupJobEditor,
    setupWorkspaceCalendars,
    setupJobStatusPicker,
    setupCalendarDialogs,
    setupJobTabs
};
